import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Pencil,
  Pause,
  Play,
  Archive,
  X,
  Upload,
  FileText,
  Loader2,
  Sparkles,
  ArrowRight,
  AlertCircle,
  Inbox,
  Link2,
  Eye,
  EyeOff,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatEUR, countryFlag } from "../lib/format";
import StockFeedPanel from "../components/StockFeedPanel";
import StockFeedStatusCard from "../components/StockFeedStatusCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import OpportunityBadge from "../components/OpportunityBadge";

const STATUS_STYLES = {
  available:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  reserved:     "bg-amber-50 text-amber-700 border-amber-200",
  incoming:     "bg-sky-50 text-sky-700 border-sky-200",
  sold:         "bg-slate-100 text-slate-500 border-slate-200",
  unavailable:  "bg-orange-50 text-orange-700 border-orange-200",
  archived:     "bg-slate-100 text-slate-400 border-slate-200",
};

const FUELS = ["benzina", "diesel", "ibrida", "elettrica", "gpl"];
const TRANSMISSIONS = ["manuale", "automatico"];

export default function MyStockPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [sellerPratiche, setSellerPratiche] = useState([]);
  const [feedPanelOpen, setFeedPanelOpen] = useState(false);
  const [feed, setFeed] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const reload = () => {
    api
      .get("/dealers/me/vehicles")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  };

  const toggleSelected = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = (list) => {
    if (!list) return;
    const eligible = list.filter((v) => v.status !== "archived").map((v) => v.id);
    setSelectedIds(new Set(eligible));
  };
  const bulkTogglePublished = async (published) => {
    if (!selectedIds.size) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/dealers/me/vehicles/bulk-publish", {
        ids: Array.from(selectedIds),
        published,
      });
      toast.success(
        t(
          published ? "my_stock.bulk_publish_ok" : "my_stock.bulk_unpublish_ok",
          { count: data.updated.length },
        ),
      );
      clearSelection();
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    if (user?.dealer_id) {
      reload();
      api
        .get("/pratiche", { params: { role: "seller" } })
        .then((r) => setSellerPratiche(r.data || []))
        .catch(() => setSellerPratiche([]));
    } else {
      setItems([]);
      setSellerPratiche([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.dealer_id]);

  useEffect(() => {
    if (user?.dealer_id) {
      api
        .get("/dealers/me/stock-feed")
        .then((r) => setFeed(r.data || null))
        .catch(() => setFeed(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.dealer_id]);

  if (!user) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!user.dealer_id && user.role !== "admin") {
    return (
      <div className="py-10" data-testid="my-stock-empty-tenant">
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6 max-w-2xl">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold">
            <AlertCircle className="w-3 h-3" /> {t("my_stock.no_tenant_overline")}
          </div>
          <h2 className="mt-2 font-heading font-bold text-2xl tracking-tight text-slate-950">
            {t("my_stock.no_tenant_title")}
          </h2>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            {t("my_stock.no_tenant_desc")}
          </p>
        </div>
      </div>
    );
  }

  if (items === null) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const canWrite = user.dealer_role === "owner" || user.dealer_role === "sales" || user.role === "admin";

  const backendBase = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="py-6 md:py-10" data-testid="my-stock-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
            {t("my_stock.overline")}
          </p>
          <h1 className="mt-1 font-heading font-bold tracking-tighter text-3xl md:text-4xl text-slate-950">
            {t("my_stock.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">{t("my_stock.subtitle")}</p>
          <p
            data-testid="my-stock-free-hint"
            className="mt-3 text-[11px] md:text-xs leading-snug text-slate-500 max-w-2xl"
          >
            <span className="text-emerald-700 font-medium">{t("pricing_hint.free_publish_full")}</span>
            <span aria-hidden className="mx-1.5 text-slate-300">·</span>
            <Link
              to="/tariffario"
              data-testid="my-stock-free-hint-tariffario"
              className="font-semibold text-blue-600 hover:text-blue-700 underline-offset-2 hover:underline"
            >
              {t("pricing_hint.see_tariffario")}
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {canWrite && (
            <>
              <Button
                data-testid="my-stock-feed-btn"
                onClick={() => setFeedPanelOpen((v) => !v)}
                variant="outline"
                className="rounded-full"
              >
                <Link2 className="w-4 h-4 mr-1" /> {t("stock_feed.connect_stock_cta")}
              </Button>
              <Button
                data-testid="my-stock-import-btn"
                onClick={() => setImportOpen(true)}
                variant="outline"
                className="rounded-full"
              >
                <Upload className="w-4 h-4 mr-1" /> {t("my_stock.import_cta")}
              </Button>
              <Button
                data-testid="my-stock-new-btn"
                onClick={() => {
                  setEditingId(null);
                  setEditorOpen(true);
                }}
                className="rounded-full bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-1" /> {t("my_stock.new_cta")}
              </Button>
            </>
          )}
        </div>
      </div>

      {(feed || feedPanelOpen) && (
        <div className="mt-6 space-y-4" data-testid="my-stock-feed-area">
          {feed && (
            <StockFeedStatusCard
              scope="dealer"
              feed={feed}
              onSynced={() => {
                api
                  .get("/dealers/me/stock-feed")
                  .then((r) => setFeed(r.data || null))
                  .catch(() => {});
                reload();
              }}
            />
          )}
          {feedPanelOpen && (
            <StockFeedPanel
              scope="dealer"
              demoBackendBase={backendBase}
              onChanged={(f) => {
                setFeed(f);
                reload();
              }}
            />
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="mt-14 rounded-2xl bg-white border border-slate-100 p-10 text-center" data-testid="my-stock-empty">
          <Sparkles className="w-6 h-6 text-slate-300 mx-auto" />
          <h3 className="mt-3 font-heading font-semibold text-lg text-slate-900">
            {t("my_stock.empty_headline")}
          </h3>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            {t("my_stock.empty_body")}
          </p>
          {canWrite && (
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => setFeedPanelOpen(true)}
                variant="outline"
                data-testid="my-stock-empty-connect-feed"
                className="rounded-full h-11 px-6"
              >
                {t("my_stock.connect_feed")}
              </Button>
              <Button
                onClick={() => {
                  setEditingId(null);
                  setEditorOpen(true);
                }}
                className="rounded-full h-11 px-6 bg-blue-600 hover:bg-blue-700"
                data-testid="my-stock-empty-cta"
              >
                <Plus className="w-4 h-4 mr-1" /> {t("my_stock.new_cta")}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          {canWrite && (
            <div className="mt-8 flex items-center flex-wrap gap-3" data-testid="my-stock-bulkbar">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    selectedIds.size === items.filter((v) => v.status !== "archived").length
                      ? clearSelection()
                      : selectAllVisible(items)
                  }
                  data-testid="my-stock-select-all"
                  className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5"
                >
                  <input
                    type="checkbox"
                    readOnly
                    checked={
                      selectedIds.size > 0 &&
                      selectedIds.size === items.filter((v) => v.status !== "archived").length
                    }
                    className="h-3.5 w-3.5 rounded border-slate-300"
                    aria-label={t("my_stock.select_all")}
                  />
                  {t("my_stock.select_all")}
                </button>
                {selectedIds.size > 0 && (
                  <span className="text-[11px] text-slate-500">
                    · {t("my_stock.selected_count", { count: selectedIds.size })}
                  </span>
                )}
              </div>
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2 ml-auto">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => bulkTogglePublished(true)}
                    disabled={bulkBusy}
                    data-testid="my-stock-bulk-publish"
                    className="h-8 rounded-full text-[11px] font-semibold border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  >
                    <Eye className="w-3 h-3 mr-1" /> {t("my_stock.bulk_publish")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => bulkTogglePublished(false)}
                    disabled={bulkBusy}
                    data-testid="my-stock-bulk-unpublish"
                    className="h-8 rounded-full text-[11px] font-semibold border-slate-200 text-slate-700 hover:bg-slate-50"
                  >
                    <EyeOff className="w-3 h-3 mr-1" /> {t("my_stock.bulk_unpublish")}
                  </Button>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-[11px] text-slate-400 hover:text-slate-700"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="w-full">
                <p className="text-[11px] text-slate-500 leading-relaxed inline-flex items-start gap-1.5" data-testid="my-stock-publish-helper">
                  <HelpCircle className="w-3 h-3 mt-0.5 text-slate-400 flex-shrink-0" />
                  <span>{t("my_stock.publish_helper")}</span>
                </p>
              </div>
            </div>
          )}
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="my-stock-list">
            {items.map((v) => (
              <StockRow
                key={v.id}
                vehicle={v}
                canWrite={canWrite}
                selected={selectedIds.has(v.id)}
                onToggleSelected={() => toggleSelected(v.id)}
                onEdit={() => {
                  setEditingId(v.id);
                  setEditorOpen(true);
                }}
                onChanged={reload}
              />
            ))}
          </ul>
        </>
      )}

      {/* Richieste ricevute (view-only reorganization of seller pratiche) */}
      {sellerPratiche.length > 0 && (
        <section
          data-testid="my-stock-incoming-requests"
          className="mt-12 rounded-2xl bg-white border border-slate-100 p-5 md:p-6 shadow-sm"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <Inbox className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                  {t("my_stock.incoming_overline")}
                </p>
                <h2 className="mt-0.5 font-heading font-semibold text-lg tracking-tight text-slate-950">
                  {t("my_stock.incoming_title", { count: sellerPratiche.length })}
                </h2>
              </div>
            </div>
            <Link
              to="/pratiche"
              data-testid="my-stock-incoming-all"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700"
            >
              {t("my_stock.incoming_view_all")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <ul className="space-y-2">
            {sellerPratiche.slice(0, 5).map((p) => (
              <li
                key={p.id}
                data-testid={`incoming-pratica-${p.id}`}
                className="flex items-center gap-3 rounded-xl border border-slate-100 p-3 hover:bg-slate-50/60 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-semibold text-sm tracking-tight truncate">
                      {p.vehicle_snapshot?.brand} {p.vehicle_snapshot?.model}
                    </div>
                    {p.buyer_dealer?.country && (
                      <span className="text-[11px] font-semibold text-slate-500 inline-flex items-center gap-1">
                        {countryFlag(p.buyer_dealer.country)} {p.buyer_dealer.country}
                      </span>
                    )}
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wider inline-flex items-center rounded-md px-2 py-0.5 border ${
                        STATUS_STYLES[p.status] || STATUS_STYLES.available
                      }`}
                    >
                      {t(`pratiche.status.${p.status}`, p.status)}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {p.buyer_dealer?.name || "—"}
                  </div>
                </div>
                <Link
                  to={`/pratiche/${p.id}`}
                  data-testid={`incoming-pratica-open-${p.id}`}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                >
                  {t("common.open")} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <AnimatePresence>
        {editorOpen && (
          <StockEditor
            vehicleId={editingId}
            onClose={() => setEditorOpen(false)}
            onSaved={() => {
              setEditorOpen(false);
              reload();
            }}
          />
        )}
        {importOpen && (
          <ImportPanel onClose={() => setImportOpen(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function StockRow({ vehicle: v, canWrite, selected, onToggleSelected, onEdit, onChanged }) {
  const { t } = useTranslation();
  const status = v.status || "available";
  const isPublished = v.published !== false;
  const a = v.analysis || {};
  const [busy, setBusy] = useState(false);

  const doAction = async (fn) => {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      data-testid={`my-stock-row-${v.id}`}
      className={`rounded-2xl bg-white border p-4 flex flex-col gap-3 ${
        selected ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-100"
      }`}
    >
      <div className="flex items-center gap-3">
        {canWrite && status !== "archived" && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelected}
            data-testid={`my-stock-check-${v.id}`}
            aria-label={t("my_stock.select_row")}
            className="h-4 w-4 rounded border-slate-300 flex-shrink-0"
          />
        )}
        <div className="w-20 h-14 rounded-lg overflow-hidden bg-slate-100 shrink-0">
          {v.images?.[0] && (
            <img src={v.images[0]} alt="" className="w-full h-full object-cover" loading="lazy" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading font-semibold text-sm tracking-tight truncate">
              {v.brand} {v.model} {v.trim && <span className="text-slate-500">· {v.trim}</span>}
            </div>
            <span
              className={`text-[10px] font-semibold uppercase tracking-wider inline-flex items-center rounded-md px-2 py-0.5 border ${STATUS_STYLES[status] || STATUS_STYLES.available}`}
              data-testid={`my-stock-status-${v.id}`}
            >
              {t(`my_stock.status.${status}`)}
            </span>
            <span
              data-testid={`my-stock-pub-chip-${v.id}`}
              className={`text-[10px] font-semibold uppercase tracking-wider inline-flex items-center gap-1 rounded-md px-2 py-0.5 border ${
                isPublished
                  ? "bg-blue-50 text-blue-700 border-blue-200"
                  : "bg-slate-100 text-slate-500 border-slate-200"
              }`}
              title={isPublished ? t("my_stock.pub_state_published") : t("my_stock.pub_state_hidden")}
            >
              {isPublished ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
              {t(isPublished ? "my_stock.pub_state_published" : "my_stock.pub_state_hidden")}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-slate-500 truncate">
            {v.year} · {v.km?.toLocaleString("it-IT")} km · {v.fuel} · {countryFlag(v.country)} {v.country}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading font-bold text-sm tabular-nums text-slate-950">
            {formatEUR(v.price_eur)}
          </div>
          {a.ai_score != null && (
            <div className="mt-0.5 flex items-center justify-end gap-1">
              <OpportunityBadge index={a.opportunity_index} size="sm" />
              <span className="text-[10px] tabular-nums text-slate-500 font-semibold">
                {Math.round(a.ai_score)}
              </span>
            </div>
          )}
        </div>
      </div>

      {canWrite && (
        <div className="flex items-center gap-1.5 flex-wrap border-t border-slate-100 pt-2 -mx-1 px-1">
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            data-testid={`my-stock-edit-${v.id}`}
            className="text-[11px] font-semibold inline-flex items-center gap-1 rounded-md px-2 py-1 bg-slate-50 hover:bg-slate-100 text-slate-700 disabled:opacity-40"
          >
            <Pencil className="w-3 h-3" /> {t("common.edit")}
          </button>
          {status !== "archived" && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                doAction(() =>
                  api.post(`/vehicles/${v.id}/${isPublished ? "unpublish" : "publish"}`),
                )
              }
              data-testid={`my-stock-toggle-pub-${v.id}`}
              className={`text-[11px] font-semibold inline-flex items-center gap-1 rounded-md px-2 py-1 disabled:opacity-40 ${
                isPublished
                  ? "bg-slate-50 hover:bg-slate-100 text-slate-700"
                  : "bg-blue-50 hover:bg-blue-100 text-blue-700"
              }`}
              title={t("my_stock.publish_helper")}
            >
              {isPublished ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {t(isPublished ? "my_stock.action_unpublish" : "my_stock.action_publish")}
            </button>
          )}
          {status === "available" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => doAction(() => api.post(`/vehicles/${v.id}/suspend`))}
              data-testid={`my-stock-suspend-${v.id}`}
              className="text-[11px] font-semibold inline-flex items-center gap-1 rounded-md px-2 py-1 bg-orange-50 hover:bg-orange-100 text-orange-700 disabled:opacity-40"
            >
              <Pause className="w-3 h-3" /> {t("my_stock.action_suspend")}
            </button>
          )}
          {status === "unavailable" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => doAction(() => api.post(`/vehicles/${v.id}/reactivate`))}
              data-testid={`my-stock-reactivate-${v.id}`}
              className="text-[11px] font-semibold inline-flex items-center gap-1 rounded-md px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 disabled:opacity-40"
            >
              <Play className="w-3 h-3" /> {t("my_stock.action_reactivate")}
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (!window.confirm(t("my_stock.confirm_archive"))) return;
              doAction(() => api.delete(`/vehicles/${v.id}`));
            }}
            data-testid={`my-stock-archive-${v.id}`}
            className="text-[11px] font-semibold inline-flex items-center gap-1 rounded-md px-2 py-1 bg-slate-50 hover:bg-red-50 hover:text-red-700 text-slate-500 ml-auto disabled:opacity-40"
          >
            <Archive className="w-3 h-3" /> {t("my_stock.action_archive")}
          </button>
        </div>
      )}
    </li>
  );
}

function StockEditor({ vehicleId, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!vehicleId;
  const [saving, setSaving] = useState(false);
  const [imagesInput, setImagesInput] = useState("");
  const [form, setForm] = useState({
    brand: "",
    model: "",
    trim: "",
    year: new Date().getFullYear() - 1,
    km: 30000,
    fuel: "diesel",
    transmission: "automatico",
    power_cv: 150,
    color: "",
    price_eur: 15000,
    vin: "",
    description: "",
    market_value_it: "",
    market_value_de: "",
    transport_cost: 700,
    registration_cost: 650,
    vat_applicable: true,
    images: [],
    options: [],
  });

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/vehicles/${vehicleId}`).then((r) => {
      const v = r.data;
      setForm((f) => ({
        ...f,
        brand: v.brand || "",
        model: v.model || "",
        trim: v.trim || "",
        year: v.year,
        km: v.km,
        fuel: v.fuel,
        transmission: v.transmission || "automatico",
        power_cv: v.power_cv || 0,
        color: v.color || "",
        price_eur: v.price_eur,
        vin: v.vin || "",
        description: v.description || "",
        market_value_it: v.market_value_it || "",
        market_value_de: v.market_value_de || "",
        transport_cost: v.transport_cost || 700,
        registration_cost: v.registration_cost || 650,
        vat_applicable: !!v.vat_applicable,
        images: v.images || [],
        options: v.options || [],
      }));
    });
  }, [vehicleId, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        km: Number(form.km),
        power_cv: Number(form.power_cv),
        price_eur: Number(form.price_eur),
        transport_cost: form.transport_cost ? Number(form.transport_cost) : null,
        registration_cost: form.registration_cost ? Number(form.registration_cost) : null,
        market_value_it: form.market_value_it ? Number(form.market_value_it) : null,
        market_value_de: form.market_value_de ? Number(form.market_value_de) : null,
      };
      if (isEdit) {
        await api.patch(`/vehicles/${vehicleId}`, payload);
        toast.success(t("my_stock.saved"));
      } else {
        await api.post("/vehicles", payload);
        toast.success(t("my_stock.created"));
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center"
      data-testid="my-stock-editor"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60 }}
        animate={{ y: 0 }}
        exit={{ y: 60 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl md:rounded-2xl md:max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 flex items-center justify-between p-4">
          <h2 className="font-heading font-bold text-lg tracking-tight">
            {isEdit ? t("my_stock.editor_edit_title") : t("my_stock.editor_new_title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800"
            data-testid="my-stock-editor-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t("my_stock.field_brand")}>
              <Input
                data-testid="stock-editor-brand"
                required
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
              />
            </Field>
            <Field label={t("my_stock.field_model")}>
              <Input
                data-testid="stock-editor-model"
                required
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
              />
            </Field>
            <Field label={t("my_stock.field_trim")}>
              <Input value={form.trim} onChange={(e) => setForm({ ...form, trim: e.target.value })} />
            </Field>
            <Field label={t("my_stock.field_color")}>
              <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
            </Field>
            <Field label={t("my_stock.field_year")}>
              <Input
                type="number"
                required
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
              />
            </Field>
            <Field label={t("my_stock.field_km")}>
              <Input
                type="number"
                required
                value={form.km}
                onChange={(e) => setForm({ ...form, km: e.target.value })}
              />
            </Field>
            <Field label={t("my_stock.field_fuel")}>
              <select
                data-testid="stock-editor-fuel"
                value={form.fuel}
                onChange={(e) => setForm({ ...form, fuel: e.target.value })}
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
              >
                {FUELS.map((f) => (
                  <option key={f} value={f}>
                    {t(`marketplace.fuel.${f}`)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("my_stock.field_transmission")}>
              <select
                value={form.transmission}
                onChange={(e) => setForm({ ...form, transmission: e.target.value })}
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
              >
                {TRANSMISSIONS.map((tv) => (
                  <option key={tv} value={tv}>
                    {tv}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("my_stock.field_power")}>
              <Input
                type="number"
                value={form.power_cv}
                onChange={(e) => setForm({ ...form, power_cv: e.target.value })}
              />
            </Field>
            <Field label={t("my_stock.field_price")}>
              <Input
                data-testid="stock-editor-price"
                type="number"
                required
                value={form.price_eur}
                onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
              />
            </Field>
            <Field label={t("my_stock.field_vin")}>
              <Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} />
            </Field>
            <Field label={t("my_stock.field_market_value_it")}>
              <Input
                type="number"
                value={form.market_value_it}
                onChange={(e) => setForm({ ...form, market_value_it: e.target.value })}
              />
            </Field>
            <Field label={t("my_stock.field_market_value_de")}>
              <Input
                type="number"
                value={form.market_value_de}
                onChange={(e) => setForm({ ...form, market_value_de: e.target.value })}
              />
            </Field>
            <Field label={t("my_stock.field_transport")}>
              <Input
                type="number"
                value={form.transport_cost}
                onChange={(e) => setForm({ ...form, transport_cost: e.target.value })}
              />
            </Field>
            <Field label={t("my_stock.field_registration")}>
              <Input
                type="number"
                value={form.registration_cost}
                onChange={(e) => setForm({ ...form, registration_cost: e.target.value })}
              />
            </Field>
          </div>
          <Field label={t("my_stock.field_description")}>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Field>
          <Field label={t("my_stock.field_images_hint")}>
            <div className="flex gap-2">
              <Input
                data-testid="stock-editor-image-url"
                placeholder="https://..."
                value={imagesInput}
                onChange={(e) => setImagesInput(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (!imagesInput.trim()) return;
                  setForm({ ...form, images: [...form.images, imagesInput.trim()] });
                  setImagesInput("");
                }}
              >
                {t("my_stock.image_add")}
              </Button>
            </div>
            {form.images.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {form.images.map((u, i) => (
                  <div
                    key={i}
                    className="relative w-16 h-12 rounded-md overflow-hidden bg-slate-100 group"
                  >
                    <img src={u} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-4 h-4 inline-flex items-center justify-center text-[8px] opacity-0 group-hover:opacity-100"
                      onClick={() =>
                        setForm({ ...form, images: form.images.filter((_, j) => j !== i) })
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              data-testid="stock-editor-submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              {isEdit ? t("my_stock.save") : t("my_stock.create")}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function ImportPanel({ onClose }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const validate = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/dealers/me/import/csv/validate", fd);
      setReport(r.data.report);
      toast.success(t("my_stock.import_validated"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center"
      data-testid="my-stock-import"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60 }}
        animate={{ y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-xl md:rounded-2xl overflow-hidden"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 flex items-center justify-between p-4">
          <h2 className="font-heading font-bold text-lg tracking-tight">{t("my_stock.import_title")}</h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 text-sm text-slate-700">
            <p>{t("my_stock.import_note")}</p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/dealers/me/import/csv/template`}
              className="mt-2 inline-flex items-center gap-1 text-blue-600 font-semibold text-xs"
              data-testid="my-stock-import-template"
              onClick={(e) => {
                e.preventDefault();
                api
                  .get("/dealers/me/import/csv/template", { responseType: "blob" })
                  .then((r) => {
                    const url = window.URL.createObjectURL(new Blob([r.data]));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "autexio_stock_template.csv";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                  });
              }}
            >
              <FileText className="w-3.5 h-3.5" /> {t("my_stock.import_download_template")}
            </a>
          </div>
          <div>
            <Label className="text-xs">{t("my_stock.import_choose_csv")}</Label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm"
              data-testid="my-stock-import-file"
            />
          </div>
          {report && (
            <div className="rounded-xl bg-white border border-slate-100 p-4 text-sm">
              <div className="font-semibold">
                {t("my_stock.import_result", { valid: report.valid_rows, total: report.total_rows })}
              </div>
              {report.missing_required?.length > 0 && (
                <p className="mt-1 text-xs text-red-600">
                  {t("my_stock.import_missing_required")}: {report.missing_required.join(", ")}
                </p>
              )}
              {report.unknown_columns?.length > 0 && (
                <p className="mt-1 text-xs text-slate-500">
                  {t("my_stock.import_unknown_columns")}: {report.unknown_columns.join(", ")}
                </p>
              )}
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {report.preview.slice(0, 20).map((r) => (
                  <li key={r.row} className={r.ok ? "text-slate-600" : "text-red-600"}>
                    row {r.row}: {r.ok ? "✓ " + (r.data.brand || "") + " " + (r.data.model || "") : r.errors.join("; ")}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              {t("common.close")}
            </Button>
            <Button
              disabled={!file || busy}
              onClick={validate}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="my-stock-import-validate"
            >
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {t("my_stock.import_validate_cta")}
            </Button>
          </div>
          <p className="text-[11px] italic text-slate-400 leading-snug border-t border-slate-100 pt-3">
            {t("my_stock.import_future_note")}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
